// File Location: src/component/policy/CounsellingFilter.js

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Filter,
  Calendar,
  Clock,
  Users,
  AlertTriangle,
  CheckCircle,
  Search,
  X,
  RefreshCw
} from 'lucide-react';
import { getApplicationsWithPendingCounselling } from '../../services/policyWriterService';

const CounsellingFilter = React.memo(({ onFilterChange, onApplicationsLoad, quickBucket, onStatsChange }) => {
  const [filters, setFilters] = useState({
    counsellingType: 'all', // 'all', 'kyp-initial-counselling', 'discharge-counselling'
    priority: 'all', // 'all', 'normal', 'urgent', 'emergency'
    emergencyStatus: 'all', // 'all', 'emergency', 'non-emergency'
    insuranceCompany: 'all',
    searchTerm: '',
    dateRange: 'all' // 'all', 'today', 'week', 'month'
  });

  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    kypInitialPending: 0,
    dischargePending: 0,
    urgent: 0,
    emergency: 0
  });
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);

  // Load filtered applications
  const loadApplications = async (isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Convert filter format to match service function
      const serviceFilters = {
        counsellingType: filters.counsellingType === 'all' ? '' : filters.counsellingType,
        priority: filters.priority === 'all' ? '' : filters.priority,
        emergency: filters.emergencyStatus === 'all' ? '' : (filters.emergencyStatus === 'emergency' ? 'true' : 'false'),
        insuranceCompany: filters.insuranceCompany === 'all' ? '' : filters.insuranceCompany,
        searchTerm: filters.searchTerm || ''
      };

      console.log('CounsellingFilter: Loading applications with filters:', serviceFilters);
      const result = await getApplicationsWithPendingCounselling(serviceFilters, 1, 50);
      
      if (result.success) {
        onApplicationsLoad(result.applications);
        const nextStats = {
          total: result.counsellingCounts.total || 0,
          kypInitialPending: result.counsellingCounts.kypInitial || 0,
          dischargePending: result.counsellingCounts.discharge || 0,
          urgent: result.applications.filter(app => app.priority === 'urgent').length,
          emergency: result.applications.filter(app => app.emergency === true).length
        };
        setStats(nextStats);
        if (typeof onStatsChange === 'function') {
          onStatsChange(nextStats);
        }
        
        // Extract unique insurance companies
        const companies = [...new Set(result.applications.map(app => app.policy?.insuranceCompany).filter(Boolean))];
        setInsuranceCompanies(companies);
      } else {
        console.error('CounsellingFilter: Failed to load applications:', result.error);
        onApplicationsLoad([]);
      }
    } catch (error) {
      console.error('CounsellingFilter: Error loading applications:', error);
      onApplicationsLoad([]);
    } finally {
      setLoading(false);
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  };

  // Handle filter changes
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      onFilterChange(newFilters);
      return newFilters;
    });
  }, [onFilterChange]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    const resetFilters = {
      counsellingType: 'all',
      priority: 'all',
      emergencyStatus: 'all',
      insuranceCompany: 'all',
      searchTerm: '',
      dateRange: 'all'
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  }, [onFilterChange]);

  // Initial load only once
  useEffect(() => {
    loadApplications(true);
  }, []);

  // Load applications when filters change with debouncing
  useEffect(() => {
    // Only run after initial loading is complete
    if (!initialLoading) {
      const debounceTimer = setTimeout(() => {
        loadApplications(false);
      }, 300); // Add debounce to avoid too many API calls

      return () => clearTimeout(debounceTimer);
    }
  }, [filters.counsellingType, filters.priority, filters.emergencyStatus, filters.insuranceCompany, filters.searchTerm, filters.dateRange]);

  // Respond to quickBucket changes from parent (bucket bar)
  useEffect(() => {
    if (!quickBucket) return;
    if (quickBucket === 'all') {
      clearFilters();
      return;
    }
    if (quickBucket === 'kyp') {
      handleFilterChange('counsellingType', 'kyp-initial');
      return;
    }
    if (quickBucket === 'discharge') {
      handleFilterChange('counsellingType', 'discharge');
      return;
    }
    if (quickBucket === 'emergency') {
      handleFilterChange('emergencyStatus', 'emergency');
      return;
    }
    if (quickBucket === 'urgent') {
      handleFilterChange('priority', 'urgent');
      return;
    }
  }, [quickBucket, clearFilters, handleFilterChange]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Filter Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Counselling Applications</h3>
              {loading && !initialLoading && (
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  <span>Updating...</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => loadApplications(false)}
              disabled={loading || initialLoading}
              className="flex items-center space-x-2 px-3 py-1.5 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${(loading || initialLoading) ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      {showFilters && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Applications
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  placeholder="Search by patient name, application ID, or hospital..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Counselling Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Counselling Type
              </label>
              <select
                value={filters.counsellingType}
                onChange={(e) => handleFilterChange('counsellingType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="kyp-initial">KYP Initial Counselling</option>
                <option value="discharge">Discharge Counselling</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Priorities</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>

            {/* Emergency Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emergency Status
              </label>
              <select
                value={filters.emergencyStatus}
                onChange={(e) => handleFilterChange('emergencyStatus', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="emergency">Emergency</option>
                <option value="non-emergency">Non-Emergency</option>
              </select>
            </div>

            {/* Insurance Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Insurance Company
              </label>
              <select
                value={filters.insuranceCompany}
                onChange={(e) => handleFilterChange('insuranceCompany', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Companies</option>
                {insuranceCompanies.map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application Date
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {stats.total} applications found
              </span>
            </div>
            <button
              onClick={clearFilters}
              className="flex items-center space-x-2 px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="h-4 w-4" />
              <span>Clear Filters</span>
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {initialLoading && (
        <div className="px-6 py-8 text-center">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading applications...</p>
        </div>
      )}
    </div>
  );
});

export default CounsellingFilter;
